/**
 * AI token usage for /admin/ai/usage.
 *
 * Read straight from Supabase: ai_usage_by_user and ai_usage_by_workspace are
 * SECURITY DEFINER and check is_platform_master() themselves, so the database is
 * the boundary and no HTTP endpoint is needed — the same shape as
 * syncScheduleService.
 *
 * @module services/aiUsageService
 */

import { getSupabaseClient } from "@/config/supabase";
import { logError } from "@/services/logger";

export interface AiUsageRow {
  /** user id, workspace id, or null for the unattributed bucket. */
  key: string | null;
  label: string;
  calls: number;
  promptTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  totalTokens: number;
  lastUsed: string | null;
  /** Per-workspace rows only: how many people contributed. */
  users?: number;
}

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/** Shown instead of a blank cell, so unattributed usage is still countable. */
export const NO_WORKSPACE_LABEL = "(ไม่ระบุ workspace)";

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

type UsageKind = "ai_usage_by_user" | "ai_usage_by_workspace";

/**
 * Row-shaping only — this never calls .rpc() itself, so the two exports below
 * are each free to pass their own LITERAL RPC name straight to .rpc(). A
 * shared helper that took the name as a variable would be invisible to
 * rpcArgumentNames.test.ts, which reads source text and cannot resolve a
 * variable — that guard now also fails loudly on any `.rpc()` call whose
 * first argument isn't a literal, so a shape like that won't pass silently.
 */
/**
 * `ai_usage` carries no foreign keys, and neither deleting a workspace nor
 * deleting a user touches it — so a row keeps a live id whose join partner is
 * gone. Falling back on the missing NAME filed that usage under "never had
 * one", which is a different claim: in the audited case 93.9% of the pie moved
 * into the unattributed bucket, and two deleted workspaces drew two slices with
 * the same caption. The id is still the attribution, so the id decides.
 */
const deletedLabel = (kind: UsageKind, id: string): string =>
  kind === "ai_usage_by_workspace"
    ? `workspace ที่ถูกลบ · ${id.slice(0, 8)}`
    : `ผู้ใช้ที่ถูกลบ · ${id.slice(0, 8)}`;

const mapRow = (kind: UsageKind, r: Record<string, unknown>): AiUsageRow => ({
  key: (r.user_id ?? r.workspace_id ?? null) as string | null,
  label:
    (r.email as string | null) ??
    (r.workspace_name as string | null) ??
    (((r.user_id ?? r.workspace_id) as string | null)
      ? deletedLabel(kind, (r.user_id ?? r.workspace_id) as string)
      : kind === "ai_usage_by_workspace" ? NO_WORKSPACE_LABEL : "(ไม่ทราบผู้ใช้)"),
  calls: num(r.calls),
  promptTokens: num(r.prompt_tokens),
  outputTokens: num(r.output_tokens),
  thinkingTokens: num(r.thinking_tokens),
  totalTokens: num(r.total_tokens),
  lastUsed: (r.last_used as string | null) ?? null,
  ...(kind === "ai_usage_by_workspace" ? { users: num(r.users) } : {}),
});

export const listAiUsageByUser = async (days: number): Promise<ServiceResult<AiUsageRow[]>> => {
  try {
    const { data, error } = await getSupabaseClient().rpc("ai_usage_by_user", { p_days: days });
    if (error) return { success: false, error: error.message };

    const rows = (data ?? []) as Array<Record<string, unknown>>;
    return { success: true, data: rows.map((r) => mapRow("ai_usage_by_user", r)) };
  } catch (e) {
    logError("AiUsage", "ai_usage_by_user failed", e);
    return { success: false, error: e instanceof Error ? e.message : "Failed to load AI usage" };
  }
};

export const listAiUsageByWorkspace = async (days: number): Promise<ServiceResult<AiUsageRow[]>> => {
  try {
    const { data, error } = await getSupabaseClient().rpc("ai_usage_by_workspace", { p_days: days });
    if (error) return { success: false, error: error.message };

    const rows = (data ?? []) as Array<Record<string, unknown>>;
    return { success: true, data: rows.map((r) => mapRow("ai_usage_by_workspace", r)) };
  } catch (e) {
    logError("AiUsage", "ai_usage_by_workspace failed", e);
    return { success: false, error: e instanceof Error ? e.message : "Failed to load AI usage" };
  }
};
